import { Autorenew } from '@mui/icons-material';
import { IconButton, keyframes, styled, Tooltip } from '@mui/material';
import { DASHBOARD_VERSION_POLL_INTERVAL_MS } from '@/hooks/useDataPolling/pollingConstants';

const spin = keyframes({ from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } });

const REFRESH_SECONDS = Math.round(DASHBOARD_VERSION_POLL_INTERVAL_MS / 1000);

// Slow on purpose: this sits on screen for as long as the dashboard is open, so it should read as
// a heartbeat rather than a spinner demanding attention. Held still for anyone who asked the OS
// to keep motion down.
const SpinningIcon = styled(Autorenew, { shouldForwardProp: property => property !== 'spinning' })<{
  spinning: boolean;
}>(({ spinning }) => ({
  ...(spinning && { animation: `${spin} 3s linear infinite` }),
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
}));

export const DashboardAutoRefreshToggle = ({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (next: boolean) => void;
}) => (
  <Tooltip
    title={
      enabled
        ? `Updating on its own, about every ${REFRESH_SECONDS} seconds. Click to hold the numbers still.`
        : 'Holding still. Click to let the dashboard update on its own again.'
    }
    placement='left'
  >
    <IconButton
      size='small'
      aria-label={enabled ? 'Turn off auto update' : 'Turn on auto update'}
      aria-pressed={enabled}
      onClick={() => onChange(!enabled)}
      data-testid='dashboard-auto-refresh-toggle'
      color={enabled ? 'secondary' : 'default'}
    >
      <SpinningIcon
        spinning={enabled}
        fontSize='small'
        data-spinning={enabled}
        data-testid='dashboard-auto-refresh-icon'
      />
    </IconButton>
  </Tooltip>
);
